import React from 'react';
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Card,
  CardContent,
  Grid
} from '@mui/material';
import {
  Help as HelpIcon,
  Info as InfoIcon,
  Build as BuildIcon,
  DataObject as DataObjectIcon,
  Assignment as AssignmentIcon,
  PersonOutline as PersonOutlineIcon,
  LocalHospital as LocalHospitalIcon,
  Psychology as PsychologyIcon,
  Lightbulb as LightbulbIcon,
  Computer as ComputerIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

const Hl7HelpDialog = ({ helpDialogOpen, setHelpDialogOpen }) => {
  const { t } = useTranslation();

  return (
    <Dialog open={helpDialogOpen} onClose={() => setHelpDialogOpen(false)} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ 
        backgroundColor: 'var(--rd-bg-subtle)', 
        borderBottom: '3px solid var(--rd-primary)',
        textAlign: 'center',
        py: 3
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
          <HelpIcon sx={{ fontSize: 32, color: 'var(--rd-primary)' }} />
          <Typography variant="h4" sx={{ color: 'var(--rd-primary)', fontWeight: 'bold' }}>
            {t('admin.hl7Mapping.help.title')}
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ p: 0, backgroundColor: 'var(--rd-bg-subtle)' }}>
        <Box sx={{ p: 3 }}>
          {/* Introduction */}
          <Card elevation={2} sx={{ mb: 3, border: '1px solid var(--rd-divider)' }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <InfoIcon sx={{ fontSize: 28, color: 'var(--rd-primary)', mr: 2 }} />
                <Typography variant="h5" sx={{ color: 'var(--rd-primary)', fontWeight: 'bold' }}>
                  {t('admin.hl7Mapping.help.introduction.title')}
                </Typography>
              </Box>
              <Typography variant="body1" sx={{ lineHeight: 1.6, color: 'var(--rd-text-secondary)' }}>
                {t('admin.hl7Mapping.help.introduction.content')}
              </Typography>
            </CardContent>
          </Card>

          {/* Basic Fields */}
          <Card elevation={2} sx={{ mb: 3, border: '1px solid var(--rd-divider)' }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <BuildIcon sx={{ fontSize: 28, color: 'var(--rd-primary)', mr: 2 }} />
                <Typography variant="h5" sx={{ color: 'var(--rd-primary)', fontWeight: 'bold' }}>
                  {t('admin.hl7Mapping.help.basicFields.title')}
                </Typography>
              </Box>
              <Typography variant="body1" sx={{ lineHeight: 1.6, color: 'var(--rd-text-secondary)' }}>
                {t('admin.hl7Mapping.help.basicFields.content')}
              </Typography>
            </CardContent>
          </Card>

          {/* HL7 Mapping Configuration */}
          <Card elevation={2} sx={{ mb: 3, border: '1px solid var(--rd-divider)' }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <DataObjectIcon sx={{ fontSize: 28, color: 'var(--rd-primary)', mr: 2 }} />
                <Typography variant="h5" sx={{ color: 'var(--rd-primary)', fontWeight: 'bold' }}>
                  {t('admin.hl7Mapping.help.mappingConfig.title')}
                </Typography>
              </Box>
              <Typography variant="body1" sx={{ lineHeight: 1.6, color: 'var(--rd-text-secondary)', mb: 3 }}>
                {t('admin.hl7Mapping.help.mappingConfig.content')}
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Box sx={{ p: 2, backgroundColor: 'var(--rd-bg-paper)', borderRadius: 2, border: '1px solid var(--rd-divider)' }}>
                    <Typography variant="h6" sx={{ color: 'var(--rd-primary)', mb: 1, fontWeight: 'bold' }}>
                      {t('admin.hl7Mapping.segment')}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'var(--rd-text-secondary)' }}>
                      {t('admin.hl7Mapping.help.mappingConfig.segment')}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box sx={{ p: 2, backgroundColor: 'var(--rd-bg-paper)', borderRadius: 2, border: '1px solid var(--rd-divider)' }}>
                    <Typography variant="h6" sx={{ color: 'var(--rd-primary)', mb: 1, fontWeight: 'bold' }}>
                      {t('admin.hl7Mapping.counter')}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'var(--rd-text-secondary)' }}>
                      {t('admin.hl7Mapping.help.mappingConfig.counter')}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box sx={{ p: 2, backgroundColor: 'var(--rd-bg-paper)', borderRadius: 2, border: '1px solid var(--rd-divider)' }}>
                    <Typography variant="h6" sx={{ color: 'var(--rd-primary)', mb: 1, fontWeight: 'bold' }}>
                      {t('admin.hl7Mapping.field')}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'var(--rd-text-secondary)' }}>
                      {t('admin.hl7Mapping.help.mappingConfig.field')}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box sx={{ p: 2, backgroundColor: 'var(--rd-bg-paper)', borderRadius: 2, border: '1px solid var(--rd-divider)' }}>
                    <Typography variant="h6" sx={{ color: 'var(--rd-primary)', mb: 1, fontWeight: 'bold' }}>
                      {t('admin.hl7Mapping.subfield')}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'var(--rd-text-secondary)' }}>
                      {t('admin.hl7Mapping.help.mappingConfig.subfield')}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ p: 2, backgroundColor: 'var(--rd-bg-paper)', borderRadius: 2, border: '1px solid var(--rd-divider)' }}>
                    <Typography variant="h6" sx={{ color: 'var(--rd-primary)', mb: 1, fontWeight: 'bold' }}>
                      {t('admin.hl7Mapping.format')}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'var(--rd-text-secondary)' }}>
                      {t('admin.hl7Mapping.help.mappingConfig.format')}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Standard Data Fields */}
          <Card elevation={2} sx={{ mb: 3, border: '1px solid var(--rd-divider)' }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <AssignmentIcon sx={{ fontSize: 28, color: 'var(--rd-primary)', mr: 2 }} />
                <Typography variant="h5" sx={{ color: 'var(--rd-primary)', fontWeight: 'bold' }}>
                  {t('admin.hl7Mapping.help.standardFields.title')}
                </Typography>
              </Box>
              
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <Card sx={{ backgroundColor: 'var(--rd-bg-subtle)', border: '1px solid var(--rd-divider)', height: '220px' }}>
                    <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                        <PersonOutlineIcon sx={{ color: 'var(--rd-primary)', mr: 2, fontSize: 28 }} />
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>Patientendaten</Typography>
                      </Box>
                      <Typography variant="body1" sx={{ lineHeight: 2, flex: 1 }}>
                        • {t('admin.hl7Mapping.fields.patient_id')}<br/>
                        • {t('admin.hl7Mapping.fields.patient_name')}<br/>
                        • {t('admin.hl7Mapping.fields.birth_date')}<br/>
                        • {t('admin.hl7Mapping.fields.sex')}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Card sx={{ backgroundColor: 'var(--rd-bg-subtle)', border: '1px solid var(--rd-divider)', height: '220px' }}>
                    <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                        <LocalHospitalIcon sx={{ color: 'var(--rd-primary)', mr: 2, fontSize: 28 }} />
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>Untersuchung</Typography>
                      </Box>
                      <Typography variant="body1" sx={{ lineHeight: 2, flex: 1 }}>
                        • {t('admin.hl7Mapping.fields.modality')}<br/>
                        • {t('admin.hl7Mapping.fields.study_description')}<br/>
                        • {t('admin.hl7Mapping.fields.accession_number')}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Card sx={{ backgroundColor: 'var(--rd-bg-subtle)', border: '1px solid var(--rd-divider)', height: '220px' }}>
                    <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                        <PsychologyIcon sx={{ color: 'var(--rd-primary)', mr: 2, fontSize: 28 }} />
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>Klinische Daten</Typography>
                      </Box>
                      <Typography variant="body1" sx={{ lineHeight: 2, flex: 1 }}>
                        • {t('admin.hl7Mapping.fields.clinical_info')}<br/>
                        • {t('admin.hl7Mapping.fields.question')}<br/>
                        • {t('admin.hl7Mapping.fields.referring_physician')}<br/>
                        • {t('admin.hl7Mapping.fields.admission_id')}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Practical Tips */}
          <Card elevation={2} sx={{ mb: 3, border: '1px solid var(--rd-divider)' }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <LightbulbIcon sx={{ fontSize: 28, color: 'var(--rd-primary)', mr: 2 }} />
                <Typography variant="h5" sx={{ color: 'var(--rd-primary)', fontWeight: 'bold' }}>
                  {t('admin.hl7Mapping.help.tips.title')}
                </Typography>
              </Box>
              <Typography variant="body1" sx={{ lineHeight: 1.6, color: 'var(--rd-text-secondary)' }}>
                {t('admin.hl7Mapping.help.tips.content')}
              </Typography>
            </CardContent>
          </Card>

          {/* System Information */}
          <Card elevation={2} sx={{ border: '1px solid var(--rd-divider)' }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <ComputerIcon sx={{ fontSize: 28, color: 'var(--rd-primary)', mr: 2 }} />
                <Typography variant="h5" sx={{ color: 'var(--rd-primary)', fontWeight: 'bold' }}>
                  {t('admin.hl7Mapping.help.systemInfo.title')}
                </Typography>
              </Box>
              <Typography variant="body1" sx={{ lineHeight: 1.6, color: 'var(--rd-text-secondary)' }}>
                {t('admin.hl7Mapping.help.systemInfo.content')}
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 3, backgroundColor: 'var(--rd-bg-subtle)', borderTop: '1px solid var(--rd-divider)' }}>
        <Button 
          onClick={() => setHelpDialogOpen(false)} 
          variant="contained" 
          className="button"
        >
          {t('admin.hl7Mapping.help.understood')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default Hl7HelpDialog; 